"use client";

import React, {createContext, useContext, useState, useEffect, useRef, type ReactNode} from "react";
import _ from "lodash";

import {$store} from '@isudio/globals';
import {cn} from "@isudio/utils";

import {Dialog, DialogContent, DialogDescription, DialogTitle} from "./dialog";
import {DialogStack, DialogStackContent} from "./dialog-stack";
import {DialogScopedToaster} from "./dialog.scoped.toaster";

export type BaseDialogProps = {
  dialog?: {
    name: string;
    modal?: boolean;
    className?: string;
    nofitClass?: boolean;
  };
};

type DialogEntry = {
  Component: React.ComponentType<any>;
  props: any;
  onBeforeClose?: () => boolean | Promise<boolean>;
  onClose?: () => void | Promise<void>;
};

type DialogControllerContextType = {
  openDialog: <T,>(
    Component: React.ComponentType<T>,
    props?: T,
    onBeforeClose?: () => boolean | Promise<boolean>,
    onClose?: () => void | Promise<void>
  ) => void;
  closeDialog: (skipBeforeClose?: boolean) => void | Promise<void>
  setOnBeforeClose: (
    handler: () => boolean | Promise<boolean>
  ) => void;
  setOnClose: (
    handler: () => void | Promise<void>
  ) => void;
  isDialogOpen: boolean;
};

const DialogControllerContext = createContext<DialogControllerContextType | null>(null);

export const useDialogController = () => {
  const ctx = useContext(DialogControllerContext);
  if (!ctx) throw new Error("useDialogController must be used inside provider");
  return ctx;
};

export function DialogControllerProvider({children}: {
  children: ReactNode
}) {
  const [dialogs, setDialogs] = useState<DialogEntry[]>([]);
  const dialogsRef = useRef<DialogEntry[]>([]);

  useEffect(() => {
    dialogsRef.current = dialogs;
  }, [dialogs]);

  const openDialog = <T,>(
    Component: React.ComponentType<T>,
    props?: T,
    onBeforeClose?: () => boolean | Promise<boolean>,
    onClose?: () => void | Promise<void>
  ) => {
    setDialogs((prev) => {
      const next = [
        ...prev,
        {
          Component,
          props: props ?? ({} as T),
          onBeforeClose,
          onClose,
        },
      ];
      $store.App.activeDialogId = `dialog-${next.length - 1}`;
      return next;
    });
  };

  const closeDialog = async (skipBeforeClose?: boolean) => {
    const current = dialogsRef.current;
    if (!current.length) return;

    const last = current[current.length - 1];
    if (!last) return;

    const isRootDialog = dialogsRef.current.length === 1;

    if (!skipBeforeClose && isRootDialog && last.onBeforeClose) {
      const shouldClose = await last.onBeforeClose();
      if (!shouldClose) return;
    }
    setDialogs((prev) => {
      if (prev.length === 0) return prev;

      const next = prev.slice(0, -1);
      $store.App.activeDialogId =
        next.length > 0 ? `dialog-${next.length - 1}` : null;

      return next;
    });

    queueMicrotask(() => {
      last.onClose?.();
    });
  };

  const setOnBeforeClose = (
    handler: () => boolean | Promise<boolean>
  ) => {
    setDialogs((prev) => {
      if (prev.length === 0) return prev;

      const updated: DialogEntry[] = [...prev];
      const lastIndex = updated.length - 1;
      const last = updated[lastIndex];
      if (!last) return prev;
      if (last.onBeforeClose === handler) return prev;

      updated[lastIndex] = {
        ...last,
        onBeforeClose: handler,
      };

      return updated;
    });
  };

  const setOnClose = (
    handler: () => void | Promise<void>
  ) => {
    setDialogs((prev) => {
      if (!prev.length) return prev;

      const updated: DialogEntry[] = [...prev];
      const lastIndex = updated.length - 1;
      const last = updated[lastIndex];
      if (!last) return prev;
      if (last.onClose === handler) return prev;

      updated[lastIndex] = {
        ...last,
        onClose: handler,
      };

      return updated;
    });
  };

  const isDialogOpen = dialogs.length > 0;

  useEffect(() => {
    if (dialogs.length === 0) return;

    const onBlock = (e: WheelEvent | TouchEvent) => {
      const target = e.target as HTMLElement;

      // ✅ allow scroll INSIDE dialog
      if (target.closest('[data-dialog-content]')) {
        return;
      }

      // ❌ block everything else (background / Spline)
      e.preventDefault();
      e.stopImmediatePropagation();
    };

    const onKeyDown = async (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;

      const activeId = $store.App.activeDialogId;
      const topId = `dialog-${dialogs.length - 1}`;

      if (activeId !== topId) return;

      e.preventDefault();
      e.stopPropagation();

      await closeDialog();
    };

    window.addEventListener('wheel', onBlock, {
      passive: false,
      capture: true,
    });

    window.addEventListener('touchmove', onBlock, {
      passive: false,
      capture: true,
    });

    window.addEventListener("keydown", onKeyDown, true);

    return () => {
      window.removeEventListener('wheel', onBlock, true);
      window.removeEventListener('touchmove', onBlock, true);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [dialogs.length]);

  return (
    <DialogControllerContext.Provider
      value={{
        openDialog,
        closeDialog,
        setOnBeforeClose,
        setOnClose,
        isDialogOpen
      }}>
      {children}

      <DialogStack
        open={dialogs.length > 0}
        dialogs={dialogs}
        closeDialog={closeDialog}>
        {dialogs.map(({
                        Component,
                        props
                      }, index) => {
          // Extract dialog options
          const options = props?.dialog ?? {};
          const name = options.name;
          const className = options.className;
          const isModal = options.modal === true;
          const dialogZIndex = 50 + index * 10;
          const isDimmed = index < dialogs.length - 1;
          const currentDialogId = `dialog-${index}`;

          return (
            <Dialog
              key={index}
              open>
              <DialogStackContent
                index={index}>
                <DialogContent
                  showOverlay={false}
                  onClose={closeDialog}
                  className={cn(
                    `dialog ${name.toLowerCase().replace(/ /g, "-")}`,
                    options?.nofitClass ? "nofit" : "fit",
                    options?.userClass ? `user-${_.isUserAvailable() ? "available" : "unavailable"}` : ""
                  )}
                  zIndex={dialogZIndex}
                  isDimmed={isDimmed}
                  // safeguard for modal dialogs
                  onInteractOutside={async (e) => {
                    if (isModal) {
                      e.preventDefault(); // stop closing
                    } else {
                      await closeDialog(); // allow close
                    }
                  }}
                >
                  <DialogTitle
                    className="sr-only">{name}</DialogTitle>
                  <DialogDescription
                    className="sr-only">{name}</DialogDescription>
                  {/* Pass all props except .dialog down to the component */}
                  <div
                    className={cn(
                      "dialog-container relative w-full h-full",
                      className
                    )}
                    style={options?.nofitClass ? {} : {
                      transform: `scale(${1 - index * 0.04})`,
                      transformOrigin: "center"
                    }}
                  >
                    <Component
                      {...Object.fromEntries(
                        Object.entries(props ?? {}).filter(([k]) => k !== "dialog")
                      )}
                      dialogOptions={options}
                      __toastContainerId={currentDialogId}
                    />
                    <DialogScopedToaster containerId={currentDialogId} />
                  </div>
                </DialogContent>
              </DialogStackContent>
            </Dialog>
          );
        })}
      </DialogStack>
    </DialogControllerContext.Provider>
  );
}
